/**
 * LES AXES DE LA MATRICE DE DÉCLINAISONS — ce qui décide des lignes et des colonnes, HORS de l'écran.
 *
 * ⚠️ MODULE PUR, SÉPARÉ DE `Variants` : la matrice à deux axes est la seule partie du composant qui
 * DÉCIDE quelque chose — quelles lignes, dans quel ordre, quelle case naît au scan, combien de cases
 * une couleur ajoute — et un test ne doit pas monter un écran React pour vérifier qu'une longueur
 * hors grille reste visible. L'écran ne fait que dessiner ce que ce module a rangé.
 *
 * ⚠️ ON STOCKE SÉPARÉ, ON AFFICHE COMBINÉ : `size` et `size2` sont deux colonnes en base, et c'est
 * l'ARTICLE (par la grille de second axe qu'il porte) qui dit si la matrice a des lignes par couleur —
 * jamais la déclinaison. Le libellé industrie « 40/32 » n'existe qu'à l'écran.
 */
import type { ArticleVariant, SecondDimension } from '../api';
import { fieldLabel } from '../vocabulary';
import { t } from '../i18n';

/** Ce qui remplace une couleur, une taille ou une valeur absente — jamais une chaîne vide, invisible. */
export const SANS = '—';

/** Une case à créer, telle que la route de création la reçoit. */
export interface CellToCreate { color: string | null; size: string | null; size2: string | null }

/** La clé d'une case — UN SEUL endroit la compose, sinon deux formats finissent par coexister. */
export const variantKey = (color: string | null, size: string | null, size2: string | null): string =>
  `${color ?? SANS}|${size ?? SANS}|${size2 ?? SANS}`;

/**
 * LE LIBELLÉ INDUSTRIE d'une déclinaison à deux axes : « 40/32 » — la taille, puis la valeur sans son
 * `L` de tête (« L32 » se lit « 32 pouces » dans le métier). Sans seconde dimension, la taille seule.
 */
export const industryLabel = (size: string | null, size2: string | null): string =>
  (size2 ? `${size ?? SANS}/${size2.replace(/^L/, '')}` : (size ?? SANS));

/**
 * LES COLONNES : l'ordre DÉCLARÉ par la grille d'abord, puis les tailles vues hors grille dans leur
 * ordre d'apparition. Rangées par l'alphabet, L passerait avant M et S avant XS ; sans grille, on
 * retombe sur l'apparition — et on ne prétend pas que c'est un ordre de tailles.
 */
export function sizeColumns(rows: readonly Pick<ArticleVariant, 'size'>[], sizeOrder: readonly string[]): string[] {
  const seen = [...new Set(rows.map((v) => v.size ?? SANS))];
  const declared = sizeOrder.filter((size) => seen.includes(size));
  return [...declared, ...seen.filter((size) => !declared.includes(size))];
}

/**
 * LES LIGNES DE CHAQUE BLOC DE COULEUR : les valeurs DÉCLARÉES de la dimension, dans leur ordre, puis
 * ce que les déclinaisons portent EN PLUS — une valeur hors grille est une déclinaison réelle, la
 * taire ferait disparaître un stock — et « — » en dernier si des déclinaisons n'ont AUCUNE seconde
 * dimension sur une grille qui en a une (une reprise qui n'a pas su découper, typiquement).
 * ⚠️ Toutes les valeurs déclarées sont rendues, même sans déclinaison : c'est précisément la ligne
 * « non créée » que le scan ouvre.
 */
export function secondAxisRows(rows: readonly Pick<ArticleVariant, 'size2'>[], dimension: SecondDimension): string[] {
  const seen = [...new Set(rows.map((v) => v.size2 ?? SANS))];
  const extra = seen.filter((v) => v !== SANS && !dimension.values.includes(v));
  return [...dimension.values, ...extra, ...(seen.includes(SANS) ? [SANS] : [])];
}

/** Les trois états d'une case, ceux de la légende du dessin. */
export type CellState = 'stocked' | 'created' | 'missing';

/** L'état d'une case. ⚠️ `null` (jamais comptée) n'est PAS « en stock » : sans mesure, rien n'est en stock. */
export const cellState = (v: Pick<ArticleVariant, 'stockQty'> | undefined): CellState =>
  (!v ? 'missing' : (v.stockQty ?? 0) > 0 ? 'stocked' : 'created');

/** Le total d'un bloc de couleur — en pièces MESURÉES, et le nombre de cases jamais comptées à part. */
export function blockTotal(rows: readonly Pick<ArticleVariant, 'stockQty'>[]): { pieces: number; neverCounted: number } {
  return {
    pieces: rows.reduce((n, v) => n + (v.stockQty ?? 0), 0),
    neverCounted: rows.filter((v) => v.stockQty === null).length,
  };
}

/**
 * LES CASES QU'« AJOUTER » CRÉE pour une couleur : toute la grille, croisée avec toute la dimension.
 * ⚠️ SUR LES VALEURS DÉCLARÉES SEULEMENT — jamais sur « — » ni sur une valeur hors grille : celles-là
 * n'existent à l'écran que parce qu'une reprise les y a mises, et les recréer les multiplierait. Une
 * dimension sans valeur déclarée retombe sur une ligne à un axe : rien n'est inventé à sa place.
 * ⚠️ `SANS` redevient `null` : c'est un signe d'écran, pas une taille.
 */
export function lineToCreate(color: string, sizes: readonly string[], dimension: SecondDimension | null): CellToCreate[] {
  const values: (string | null)[] = dimension && dimension.values.length > 0 ? dimension.values : [null];
  return sizes.flatMap((size) => values.map((size2) => ({ color, size: size === SANS ? null : size, size2 })));
}

/**
 * LE RÉSUMÉ DES TAILLES D'UN MODÈLE pour le journal : « 38 → 46 », « M », et « × 30/32/34 » quand la
 * grille porte un second axe — la colonne « Tailles » de la maquette « Articles — Tailles 2 ».
 * ⚠️ PREMIÈRE ET DERNIÈRE VIENNENT DU SERVEUR, dans l'ordre de la grille : l'écran ne trie rien. Une
 *    dimension longue est ABRÉGÉE et le dit (« 18/20/22/24 +6 ») — dix valeurs ne tiennent pas dans
 *    une cellule, et une cellule tronquée en silence se lirait comme une grille de quatre longueurs.
 */
export function sizeSpanLabel(first: string | null, last: string | null, dimension: SecondDimension | null): string | null {
  if (!first) return null;
  const span = last && last !== first ? `${first} → ${last}` : first;
  if (!dimension || dimension.values.length === 0) return span;
  const values = dimension.values;
  const shown = values.length > 4 ? `${values.slice(0, 4).join('/')} +${values.length - 4}` : values.join('/');
  return `${span} × ${shown}`;
}

/**
 * CE QUE LA TÊTE DES DÉCLINAISONS ANNONCE : quelle grille, et combien d'axes.
 *
 * ⚠️ « AUCUN ORDRE DÉCLARÉ » N'EST PAS « AUCUNE GRILLE », et les confondre fait dire à l'écran le
 * contraire de la base : le serveur rend un ordre VIDE aussi bien quand rien n'est rattaché que
 * quand la grille rattachée n'a encore aucune taille active (une grille neuve, typiquement). Le
 * modèle, lui, porte bien sa grille — l'annoncer « sans grille rattachée » envoie en rattacher une
 * seconde par-dessus la première.
 */
export function axesSummary(
  gridName: string | null,
  sizeOrder: readonly string[],
  dimension: SecondDimension | null,
  gridPending = false,
): string {
  // ⚠️ LES AXES SE CHOISISSENT MAINTENANT ET S'ÉCRIVENT PLUS TARD, LA MATRICE LIT CE QUI EST ÉCRIT.
  // Tant que le pied de la fiche n'a pas enregistré, les déclinaisons ne portent ni la grille ni le
  // second axe choisis : la matrice garde donc ses axes actuels. Sans cette phrase, l'écran nomme un
  // choix au-dessus d'une matrice qui en dessine un autre — deux vérités dans le même bandeau, et un
  // opérateur qui attend en vain.
  // ⚠️ LE DRAPEAU COUVRE LES DEUX AXES : ils partent par le même pied, donc l'un comme l'autre en
  // attente met la matrice en retard, et le dire d'un seul suffirait à laisser croire l'autre écrit.
  if (gridPending && gridName) {
    return t('Grille « {gridName} » choisie, pas encore enregistrée — la matrice garde ses axes actuels jusqu’à l’enregistrement de la fiche.', { gridName });
  }
  // ⚠️ LA PHRASE S’ASSEMBLE DE CLAUSES COMPLÈTES, jamais de trous mis bout à bout. Une clé qui
  //    commence par `{head}{order}` ne dit rien au traducteur : il ne peut ni déplacer le préfixe,
  //    ni savoir quelle phrase arrive dans le trou. Ici chaque morceau se traduit seul, et ce sont
  //    le tiret et la virgule — de la ponctuation, pas du texte — qui les relient.
  const head = gridName ? `${t('Grille « {gridName} »', { gridName })} — ` : '';
  const values = dimension && dimension.values.length ? `(${dimension.values.join(', ')})` : t('(aucune valeur déclarée)');
  if (sizeOrder.length === 0) {
    const order = gridName
      ? t('aucune taille déclarée : celles des déclinaisons se lisent dans leur ordre d’apparition')
      : t('Sans grille rattachée : les tailles se lisent dans leur ordre d’apparition');
    const axe = dimension
      ? t('second axe « {v} » {values}.', { v: dimension.label.toLowerCase(), values })
      : t('sans second axe.');
    return `${head}${order}, ${axe}`;
  }
  const axe = dimension
    ? t('tailles × {v} {values}.', { v: dimension.label.toLowerCase(), values })
    : t('un seul axe de tailles.');
  return `${head}${axe}`;
}

/**
 * POURQUOI LA DIMENSION 2 N'EST PAS OFFERTE — la phrase que l'écran met à la place du choix.
 *
 * ⚠️ CE QUI N'EST PAS OFFERT SE DIT, ET DIT POURQUOI (§23.0) : un contrôle inerte sans motif envoie
 * chercher la fonction ailleurs, et un contrôle qui DISPARAÎT fait conclure qu'elle n'existe pas.
 *
 * ⚠️ IL NE DÉPEND PLUS DE LA GRILLE DE TAILLES, et c'est le renversement en une ligne : le second axe
 * est un champ de l'ARTICLE, au même titre que sa grille. Le subordonner à la première reviendrait à
 * refuser un modèle qui n'a que des longueurs, et surtout à laisser croire que les deux sont liés.
 *
 * ⚠️ IL RESTE UN CAS QUI BLOQUE, ET UN SEUL : des déclinaisons qui ne portent pas toutes le même
 * second axe. Poser un choix unique par-dessus écraserait en silence celui d'une partie du modèle.
 * ⚠️ Rendre `null` quand le choix EST offert : c'est l'appelant qui dessine alors le picker.
 */
export function secondAxisBlocker(state: {
  mixedAxes: boolean;
  gridsLoaded: boolean;
}): string | null {
  if (state.mixedAxes) return t('{v} : les déclinaisons de ce modèle n’en portent pas toutes le même — reprenez-les une à une avant d’en poser un pour tout le modèle.', { v: fieldLabel('sizeGrid2') });
  if (!state.gridsLoaded) return t('Lecture des grilles…');
  return null;
}
